/**
 * North American malts — Briess, Rahr and Great Western.
 *
 * US maltsters publish colour in degrees Lovibond and diastatic power in
 * °Lintner directly, so both are carried as given and the EBC figure is the
 * derived one: `EBC = 2.65 · °L − 1.2`, rounded to one decimal. Lintner numbers
 * therefore look high next to a German malt, which is a unit difference rather
 * than a real gulf in enzyme content.
 *
 * ## Great Western presents colour as SRM, and as a band
 *
 * GWM's product pages and sell sheets report "Color SRM" rather than
 * °Lovibond. For a malt grain (as opposed to finished beer) the two scales
 * read the same, so SRM is taken here as °Lovibond directly. Nearly every
 * other figure — colour, and sometimes extract or DP — is printed as a
 * min–max band rather than a single number; per house convention the stored
 * figure is always the band's mid-point, not either bound.
 *
 * Several GWM specialty malts (Mela, Brūmalt, Dark Munich, Biscuit Rye, Pink
 * Boots, Chit, unmalted White Wheat) publish extract or colour but not
 * diastatic power, so the DP figure there is class-typical and the row is
 * marked `representative` even though its extract is a real published number.
 * Great Western's Chit Malt publishes an extract of 50 % against its Premium
 * 2-Row's ~81 %, the same functional trade-off Bestmalz and The Swaen make
 * with their own chit malts — sold for foam and body at 10 % or less, not as
 * a fermentable base, so it lands under this catalog's usual 25 PPG floor by
 * design rather than by error.
 *
 * ## Rahr prints two specs per product, and they do not always agree
 *
 * Each Rahr product page carries a small table (colour as an SRM or °Lovibond
 * band, "Moisture % Max", "Extract FG Min", protein, usage rate) and links an
 * "Info Sheet" PDF that repeats it. The two are separate revisions and several
 * of them disagree: Old World Pilsner is 4.0 % moisture on the page and 5.0 %
 * in the sheet, Dextrin Malt is 75 % extract on the page and >79 % in the
 * sheet, High DP Distillers and Malted Oats differ on colour and moisture. The
 * sheets are also visibly template-recycled — the Malted Oats PDF still names
 * Dextrin Malt in its body copy — so the **product page table is taken as
 * primary here**, the PDF supplies only figures the page omits (diastatic
 * power, mostly), and any row where the two conflict is marked
 * `representative` because there is no way to tell which revision is current.
 *
 * Rahr publish no °Lintner figure at all for Standard 6-Row or To Thee! Pils,
 * and neither extract nor colour for Unmalted Red Wheat, so those rows carry
 * class-typical values for the missing fields. Rahr no longer lists a page for
 * Munich or Caramel 60L; both rows are kept as they were.
 *
 * ## Briess CBW® extracts: solids, not dry-basis extract
 *
 * Briess's CBW spec sheets publish "Solids %" (roughly 97–98 for DME, 79 for
 * LME) rather than a fine-grind dry-basis extract figure. Following this
 * catalog's generic-extract convention, `extractFineDryPercent` is stored as
 * 97 (the DME/malt-solids norm) and the published solids % instead sets
 * `moisturePercent` as `100 − solids`, which is what actually varies between
 * the dry and liquid forms of the same recipe.
 */

import type { Fermentable } from '../../schema/fermentable';

/** Briess, Rahr and Great Western malts. @see Fermentable */
export const northAmericanFermentables: readonly Fermentable[] = [
  {
    id: 'briess-ashburne-mild',
    name: 'Ashburne Mild Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.0,
    moisturePercent: 3.5,
    colorLovibond: 5.3,
    colorEbc: 12.8,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'One of the few mild malts an American maltster still makes, kilned past pale ale into biscuit and light toffee while keeping enough enzyme to carry a grist alone. A dark mild wants it at 80-100 %; anywhere else it is a warmer base than 2-row.',
    tags: ['base', 'mild', 'american', 'malty'],
    url: 'https://www.brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Ashburne-Mild-Malt.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-black-malt',
    name: 'Black Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 6,
    colorLovibond: 500,
    colorEbc: 1323.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Briess's standard black malt, and the husked one: sharper and more astringent than their debittered Blackprinz at the same 500 Lovibond, which is the whole reason both exist. One or two percent sets a stout's color. Briess publish moisture and color for it and no extract, so the yield here is class-typical.",
    tags: ['roasted', 'black', 'american', 'stout', 'porter'],
    url: 'https://www.brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Black-Malt.pdf',
    provenance: 'representative',
  },
  {
    id: 'briess-brewers-malt-2-row',
    name: 'Brewers Malt 2-Row',
    maker: 'Briess',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The American workhorse base malt: pale, mildly sweet and loaded with enzyme, so it will happily convert a third of its own weight in adjuncts. Suits almost any ale or lager where the malt is a backdrop rather than the point.',
    tags: ['base', 'two-row', 'american', 'high-enzyme'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Brewers-Malt.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-dark-chocolate-malt',
    name: 'Dark Chocolate Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5.5,
    colorLovibond: 430,
    colorEbc: 1138.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted past their standard chocolate malt to 430 Lovibond, where cocoa starts turning towards coffee and the finish dries out. Two to five percent in a porter or a stout. Extract is not published and is class-typical against their other roasted rows.',
    tags: ['roasted', 'chocolate', 'american', 'porter', 'cocoa'],
    url: 'https://www.brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Dark-Chocolate-Malt.pdf',
    provenance: 'representative',
  },
  {
    id: 'briess-extra-special-malt',
    name: 'Extra Special Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 5,
    colorLovibond: 130,
    colorEbc: 343.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The darker sibling of Briess's Special Roast, at 130 Lovibond against its 40: bread crust and dark caramel with a sourdough tang, and enough color to carry an amber ale on its own. Only moisture and color are published, so extract here is class-typical.",
    tags: ['roasted', 'american', 'caramel', 'bready', 'amber'],
    url: 'https://www.brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Extra-Special-Malt.pdf',
    provenance: 'representative',
  },
  {
    id: 'briess-goldpils-vienna',
    name: 'Goldpils Vienna Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.0,
    moisturePercent: 3.5,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 95,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Briess's Vienna, and paler than the European ones at 3.5 degrees Lovibond, closer to a toasted pale malt than to a light Munich. Diastatic power of 95 is high for the class, so it converts a grist of itself comfortably and leaves a toasted note at the finish.",
    tags: ['vienna', 'kilned', 'american', 'toasted'],
    url: 'https://www.brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Goldpils-Vienna-Malt.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-pilsen-malt',
    name: 'Pilsen Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 1.2,
    colorEbc: 2,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest malt Briess makes, kilned gently for a clean, faintly honeyed sweetness. High enzyme content and very low color make it the base of choice for pale lagers, pilsners and light Belgian ales.',
    tags: ['base', 'pilsner', 'lager', 'american', 'pale'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Pilsen-Malt-1.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-pale-ale-malt',
    name: 'Pale Ale Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.5,
    moisturePercent: 4,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row barley kilned harder than standard brewers malt, trading some diastatic power for a fuller, bready-biscuity flavor. Still comfortably self-converting, so it can carry an entire pale ale or IPA grist.',
    tags: ['base', 'pale-ale', 'american', 'ipa'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Pale-Ale-Malt.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-munich-malt-10l',
    // Bonlander is the name on the bag and the name a shop lists it under. The
    // id keeps its old spelling: ids are the contract recipes store.
    name: 'Bonlander Munich Malt 10L',
    maker: 'Briess',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 3.3,
    colorLovibond: 10,
    colorEbc: 25.3,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned to a deep gold with enough enzyme left to convert itself, though not much beyond that. Brings toasted-crust malt depth to Oktoberfest, bock and amber ales; 10–30 % is the usual seasoning rate.',
    tags: ['munich', 'kilned', 'american', 'malty'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Bonlander-Munich-Malt-10L.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-carapils',
    name: 'Carapils',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 1.5,
    colorEbc: 2.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dextrin malt stewed to lock in unfermentable sugars without developing color or caramel flavor. Added at 3–8 % it thickens the body and stiffens the head on beers too pale to hide a crystal malt.',
    tags: ['dextrin', 'american', 'body', 'head-retention'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Carapils-Malt.pdf',
    provenance: 'representative',
  },
  {
    id: 'briess-caramel-60',
    name: 'Caramel Malt 60L',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 60,
    colorEbc: 157.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle crystal grade and the most used of the range, giving clean caramel sweetness and a copper cast. Standard in American amber and brown ales at 5–10 %; push much past that and the beer turns cloying.',
    tags: ['crystal', 'caramel', 'american', 'amber'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Caramel-Malt-60L.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-caramel-120',
    name: 'Caramel Malt 120L',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 3,
    colorLovibond: 120,
    colorEbc: 316.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest standard crystal, tasting of raisin, burnt sugar and a touch of roast. Use 2–5 % to add red-brown depth to a brown ale, old ale or barleywine.',
    tags: ['crystal', 'caramel', 'american', 'dark', 'raisin'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Caramel-Malt-120L.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-caramel-10',
    name: 'Caramel Malt 10L',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77.0,
    moisturePercent: 7.0,
    colorLovibond: 10,
    colorEbc: 25.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The lightest of the line, and barely a color malt at all: candy-like sweetness, a golden lift and better foam. A few percent rounds out a pilsner without anyone tasting caramel.',
    tags: ['crystal', 'caramel', 'american', 'pale'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Caramel-Malt-10L.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-caramel-20',
    name: 'Caramel Malt 20L',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76.0,
    moisturePercent: 6.0,
    colorLovibond: 20,
    colorEbc: 51.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Mild caramel over a golden body. The usual choice where a pale ale needs sweetness and color but not the toffee of a 40L.',
    tags: ['crystal', 'caramel', 'american', 'pale'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Caramel-Malt-20L.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-caramel-30',
    name: 'Caramel Malt 30L',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77.0,
    moisturePercent: 5.5,
    colorLovibond: 30,
    colorEbc: 78.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Between the light and medium grades: light toffee with a deepening gold. Useful in a blonde or a cream ale that wants a little more body.',
    tags: ['crystal', 'caramel', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-caramel-40',
    name: 'Caramel Malt 40L',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77.0,
    moisturePercent: 5.5,
    colorLovibond: 40,
    colorEbc: 104.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sweet caramel and toffee at the color most American pale ales reach for. The default crystal when a recipe just says "crystal malt".',
    tags: ['crystal', 'caramel', 'american', 'toffee'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Caramel-Malt-40L.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-caramel-80',
    name: 'Caramel Malt 80L',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76.0,
    moisturePercent: 4.5,
    colorLovibond: 80,
    colorEbc: 210.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pronounced caramel with burnt sugar behind it and the first of the raisin notes. Darkens an amber ale toward red without tipping into roast.',
    tags: ['crystal', 'caramel', 'american', 'raisin'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Caramel-Malt-80L.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-caramel-90',
    name: 'Caramel Malt 90L',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 4.0,
    colorLovibond: 90,
    colorEbc: 237.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Deep caramel and burnt sugar, a half step short of the 120. Brown ales and Scottish ales, where the color has to arrive with sweetness attached.',
    tags: ['crystal', 'caramel', 'american', 'dark'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Caramel-Malt-90L.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-caracrystal-wheat',
    name: 'Caracrystal Wheat Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78.0,
    moisturePercent: 4.0,
    colorLovibond: 55,
    colorEbc: 144.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat taken through the caramel drum rather than barley. Brings the caramel sweetness of a mid crystal along with the head retention and soft body wheat is used for.',
    tags: ['crystal', 'caramel', 'wheat', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-caramel-munich-60',
    name: 'Caramel Munich Malt 60L',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77.0,
    moisturePercent: 3.5,
    colorLovibond: 60,
    colorEbc: 157.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Munich malt crystallized, so the caramel arrives with bread crust underneath it. A way to deepen a marzen or a bock without a second specialty malt.',
    tags: ['crystal', 'caramel', 'munich', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-caramel-rye',
    name: 'Caramel Rye Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 80.0,
    moisturePercent: 5.0,
    colorLovibond: 60,
    colorEbc: 157.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rye through the caramel drum: the spice and slickness of rye, with caramel sweetness in front of it. Highest extract in the caramel range.',
    tags: ['crystal', 'caramel', 'rye', 'american', 'spicy'],
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-caramel-vienne-20',
    name: 'Caramel Vienne Malt 20L',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78.0,
    moisturePercent: 4.5,
    colorLovibond: 20,
    colorEbc: 51.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A lightly crystallized Vienna: golden, gently toasty and sweeter than Vienna malt alone. Belgian pales and amber lagers.',
    tags: ['crystal', 'caramel', 'vienna', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-crystal-red',
    name: 'Crystal Red Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 3.0,
    colorLovibond: 200,
    colorEbc: 528.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The reddest malt Briess makes, and darker than any of the numbered grades. A few percent turns an amber ale genuinely red; much more and the burnt-sugar edge takes over.',
    tags: ['crystal', 'caramel', 'american', 'red', 'dark'],
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-red-wheat',
    name: 'Red Wheat Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82.0,
    moisturePercent: 5.0,
    colorLovibond: 3.0,
    colorEbc: 6.7,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The red-wheat counterpart to Briess's white, a shade darker and noticeably breadier, with the protein to build a lasting head. Same husk-free caution above 40 %, and the usual choice where a wheat beer wants more grain character than crispness.",
    tags: ['base', 'wheat', 'american', 'bready'],
    url: 'https://www.brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Wheat-Malt-Red.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-synergy-select-pilsen',
    name: 'Synergy Select Pilsen Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 83.0,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Briess's premium pilsen malt and the highest-extract row in their range at 83 %. Continental in style but American-grown, with the enzyme to carry adjuncts, and pale enough to leave the color entirely to whatever comes after it.",
    tags: ['base', 'pilsner', 'american', 'lager'],
    url: 'https://www.brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Synergy-Select-Pilsen-Malt.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-victory',
    name: 'Victory Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4,
    colorLovibond: 28,
    colorEbc: 73,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A drum-toasted malt in the biscuit family, all baked bread crust and toasted nuts with no residual sweetness. Around 5 % lends a nutty backbone to brown ales, porters and English-leaning bitters.',
    tags: ['toasted', 'biscuit', 'american', 'nutty'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Victory-Malt.pdf',
    provenance: 'representative',
  },
  {
    id: 'briess-chocolate-malt',
    name: 'Chocolate Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 350,
    colorEbc: 926.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted barley roasted to a deep brown, contributing cocoa and mild coffee notes plus a lot of color per pound. Typical additions of 2–5 % give a porter its character without the sharp bite of black malt.',
    tags: ['roasted', 'chocolate', 'american', 'porter'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Chocolate-Malt.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Briess',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 300,
    colorEbc: 793.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley taken to a dark roast, which is where dry stout gets its coffee bitterness and tan head. Sharper and drier than chocolate malt at the same color, so 3–7 % is usually enough.',
    tags: ['roasted', 'barley', 'american', 'stout', 'coffee'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Roasted-Barley.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-midnight-wheat',
    name: 'Midnight Wheat Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 60,
    moisturePercent: 6.5,
    colorLovibond: 550,
    colorEbc: 1461.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 7,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Black color without the black-malt bite: a debittered roasted wheat that gives the same depth of color as black malt and almost none of the astringency, which is what makes a black IPA taste like an IPA. One to two percent tints a beer with no flavor cost at all; five to seven brings a hint of smooth roast. Briess publishes only moisture and color for it, so the extract here is typical of the class rather than theirs.',
    tags: ['debittered', 'black', 'wheat', 'black-ipa', 'schwarzbier', 'colour-adjustment'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Midnight-Wheat-Malt.pdf',
    provenance: 'representative',
  },
  {
    id: 'briess-blackprinz',
    name: 'Blackprinz Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 60,
    moisturePercent: 6,
    colorLovibond: 500,
    colorEbc: 1328.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The hulless-barley sibling of Midnight Wheat, and the one to reach for when the grist should stay barley: deep color, a trace of mild roast, none of the acrid edge. It takes a heavier hand than the wheat version, up to ten percent, before the roast starts to speak. Extract is typical of the class; Briess prints only moisture and color.',
    tags: ['debittered', 'black', 'hulless', 'black-lager', 'schwarzbier', 'colour-adjustment'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Blackprinz-Malt.pdf',
    provenance: 'representative',
  },
  {
    id: 'briess-special-roast',
    name: 'Special Roast Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 2.5,
    colorLovibond: 40,
    colorEbc: 105.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Drum roasted to a tangy, sourdough-crust flavor that no kilned malt quite reaches \u2014 biscuit with a sharp edge behind it. Five to ten percent is where it belongs, in brown ales, porters and ESBs that want an older, breadier character than crystal malt gives.',
    tags: ['toasted', 'sourdough', 'biscuit', 'brown-ale', 'porter', 'esb'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Special-Roast-Malt.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-aromatic-munich-20l',
    name: 'Aromatic Munich Malt 20L',
    maker: 'Briess',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 2.5,
    colorLovibond: 20,
    colorEbc: 52,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Twice the color of the 10 L Munich and a good deal more of the flavor: deep golden, intensely malty, and still carrying enough diastatic power to convert itself. Five to ten percent lifts the malt in anything; Briess will take it to half the grist in a bock, where exaggerated maltiness is the point.',
    tags: ['munich', 'aromatic', 'kilned', 'malty', 'bock', 'american'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Aromatic-Munich-Malt-20L.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-rye-malt',
    name: 'Rye Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 84,
    moisturePercent: 4.5,
    colorLovibond: 3.7,
    colorEbc: 8.7,
    diastaticPowerLintner: 105,
    suggestedMaxPercent: 35,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Base malted rye, and the extract figure explains why brewers keep coming back to it: 84 percent dry basis, higher than most barley, with enough diastatic power to carry itself. It is huskless, so it turns a mash gummy in quantity \u2014 a few percent for dryness and spice, up to a third for a proper Roggenbier, and rice hulls once it is past a fifth.',
    tags: ['rye', 'base', 'spicy', 'roggenbier', 'rye-ipa', 'american'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Rye-Malt.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-white-wheat',
    name: 'White Wheat Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83.0,
    moisturePercent: 5.0,
    colorLovibond: 2.8,
    colorEbc: 6.2,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale American wheat with a very high diastatic power, 120 Lintner, which is above most base barley, so it converts itself and then some. Husk-free, so add rice hulls above about 40 %; standard at 50-60 % in an American wheat beer.',
    tags: ['base', 'wheat', 'american', 'head-retention'],
    url: 'https://www.brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Wheat-Malt-White.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-cbw-pilsen-light-lme',
    name: 'CBW Pilsen Light LME',
    maker: 'Briess',
    origin: 'United States',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 21,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Briess's lightest liquid extract, made from 99 % Pilsen malt and 1 % Carapils, at 79 % solids so a pound gives roughly 36 points. Produces a crisp, very pale wort with only a subtle malt flavor — the extract to reach for when color has to stay out of the way.",
    tags: ['extract', 'lme', 'liquid', 'cbw', 'pilsner', 'pale'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-CBW-Pilsen-Light-LME.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-cbw-pilsen-light-dme',
    name: 'CBW Pilsen Light DME',
    maker: 'Briess',
    origin: 'United States',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 3,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The dry equivalent of CBW Pilsen Light: 99 % Pilsen malt and 1 % Carapils spray-dried to 97 % solids, so it yields close to 44 points per pound, as every CBW DME does. The default choice for a starter or a very pale extract batch.',
    tags: ['extract', 'dme', 'dry', 'cbw', 'pilsner', 'pale'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-CBW-Pilsen-Light-DME.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-cbw-golden-light-lme',
    name: 'CBW Golden Light LME',
    maker: 'Briess',
    origin: 'United States',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 21,
    colorLovibond: 4,
    colorEbc: 9.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Briess's general-purpose liquid extract, 99 % base malt and 1 % Carapils at 79 % solids. A shade darker and maltier than the Pilsen Light version, and the usual all-round choice for a golden ale or a pale extract batch that wants a little more malt character.",
    tags: ['extract', 'lme', 'liquid', 'cbw', 'golden', 'pale'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-CBW-Golden-Light-LME.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-cbw-golden-light-dme',
    name: 'CBW Golden Light DME',
    maker: 'Briess',
    origin: 'United States',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 3,
    colorLovibond: 4,
    colorEbc: 9.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The dry form of CBW Golden Light: 99 % base malt and 1 % Carapils, spray-dried to 97 % solids for roughly 44 points per pound. A safe general-purpose DME for any beer style that is not asking to stay as pale as possible.',
    tags: ['extract', 'dme', 'dry', 'cbw', 'golden', 'pale'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-CBW-Golden-Light-DME.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-cbw-bavarian-wheat-lme',
    name: 'CBW Bavarian Wheat LME',
    maker: 'Briess',
    origin: 'United States',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 21,
    colorLovibond: 3,
    colorEbc: 6.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Liquid extract made from 65 % malted wheat and 35 % malted barley, at 79 % solids. Carries genuine wheat-beer character — soft, faintly fruity malt sweetness — into an extract batch, and works just as well for topping up an all-grain wheat beer.',
    tags: ['extract', 'lme', 'liquid', 'cbw', 'wheat'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-CBW-Bavarian-Wheat-LME.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-cbw-bavarian-wheat-dme',
    name: 'CBW Bavarian Wheat DME',
    maker: 'Briess',
    origin: 'United States',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 3,
    colorLovibond: 3,
    colorEbc: 6.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The dry form of Bavarian Wheat: 65 % malted wheat and 35 % malted barley spray-dried to 97 % solids. A convenient way to lift the wheat character and head retention of an otherwise all-barley extract batch without adding grain.',
    tags: ['extract', 'dme', 'dry', 'cbw', 'wheat'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-CBW-Bavarian-Wheat-DME.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-cbw-traditional-dark-lme',
    name: 'CBW Traditional Dark LME',
    maker: 'Briess',
    origin: 'United States',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 21,
    colorLovibond: 30,
    colorEbc: 78.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Briess's dark liquid extract, brewed from a grist of 54 % Bonlander Munich Malt 10L, 30 % base malt, 13 % Caramel Malt 60L and 3 % Black Malt at 79 % solids. Sweet and intensely malty with real roast and caramel depth, for red ales, bocks, doppelbocks and brown ales built entirely or partly from extract.",
    tags: ['extract', 'lme', 'liquid', 'cbw', 'dark', 'caramel'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-CBW-Traditional-Dark-LME.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-cbw-traditional-dark-dme',
    name: 'CBW Traditional Dark DME',
    maker: 'Briess',
    origin: 'United States',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 3,
    colorLovibond: 30,
    colorEbc: 78.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The dry form of Traditional Dark: the same Munich, Caramel 60L and Black Malt blend as the liquid version, spray-dried to 97 % solids for roughly 44 points per pound. Delivers a dark, sweet, intensely malty extract base for Oktoberfest, bock and brown ale without a drop of liquid syrup.',
    tags: ['extract', 'dme', 'dry', 'cbw', 'dark', 'caramel'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-CBW-Traditional-Dark-DME.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-cherrywood-smoked',
    name: 'Smoked Malt, Cherry Wood',
    maker: 'Briess',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.5,
    moisturePercent: 6.0,
    colorLovibond: 5,
    colorEbc: 12.1,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row smoked over cherry wood instead of beech, which trades the savory ham note of a Bamberg malt for something fruitier and rounder. Briess file it with the kilned malts rather than the roasted ones and it stays enzyme-active at 90 Lintner, so it can carry its own conversion at 15-30 ppm phenol. Five to ten percent seasons a Scottish ale or Oktoberfest, thirty to sixty puts real smoke into a stout or porter, and sixty percent is where Briess stop.',
    tags: ['smoked', 'cherrywood', 'rauch', 'american', 'kilned', 'base'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Smoked-Malt-Cherry-Wood.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-carabrown',
    name: 'Carabrown Malt',
    maker: 'Briess',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'roasted',
    extractFineDryPercent: 79.0,
    moisturePercent: 2.2,
    colorLovibond: 55,
    colorEbc: 144.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Briess call this a brown malt and built it on the pale side of the style, so a little residual sweetness survives under the toast rather than the dry husky bite a British brown malt brings. Graham cracker, biscuit and toasted nuts over a clean dry finish; five to ten percent warms the color, fifteen to twenty-five makes the toast the point. No diastatic power is published because a roaster leaves none.',
    tags: ['toasted', 'brown', 'biscuit', 'american', 'brown-ale', 'porter'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Carabrown-Malt.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-flaked-barley',
    name: 'Brewers Barley Flakes',
    maker: 'Briess',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75.0,
    moisturePercent: 8.0,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Barley cooked and rolled before it reaches you, so the starch is already gelatinised: no cereal mash, no milling, and conversion inside five minutes alongside the malt. Where the generic flake carries class-typical numbers, these are Briess's own — 75 % extract at 8 % moisture. Ten to twenty-five percent is the range they quote, and it is where an Irish stout gets its creamy body and its foam.",
    tags: ['adjunct', 'flaked', 'barley', 'american', 'stout', 'body'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Brewers-Barley-Flakes.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-flaked-oats',
    name: 'Brewers Oat Flakes',
    maker: 'Briess',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 70.0,
    moisturePercent: 8.0,
    colorLovibond: 2.5,
    colorEbc: 5.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dehulled oats, pre-cooked and flaked for the brewhouse, which is what buys you a normal single-infusion mash and a normal lauter. Briess put the yield below their other flakes on purpose — 70 % extract at 8 % moisture, the hull having gone — and warn that the sticky mouthfeel shows up even in small doses. Five to twenty-five percent for an oatmeal stout; a couple of percent is plenty in a wit.',
    tags: ['adjunct', 'flaked', 'oats', 'american', 'mouthfeel', 'stout', 'hazy'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Brewers-Oat-Flakes.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-flaked-red-wheat',
    name: 'Brewers Red Wheat Flakes',
    maker: 'Briess',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 70.0,
    moisturePercent: 7.0,
    colorLovibond: 2.0,
    colorEbc: 4.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Red wheat rather than white, pre-gelatinised so it goes into the mash with the malt and needs no cereal cook. Slightly nutty and a shade darker than a white flake, published at 70 % extract and 7 % moisture. Half a percent to one percent is enough to firm up the head on any beer; up to 40 % makes it the cereal adjunct of a wit, with a coarser grain flavor than wheat malt would give.',
    tags: ['adjunct', 'flaked', 'wheat', 'red-wheat', 'american', 'witbier', 'head-retention'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Brewers-Red-Wheat-Flakes.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-flaked-rye',
    name: 'Brewers Rye Flakes',
    maker: 'Briess',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 71.0,
    moisturePercent: 7.5,
    colorLovibond: 2.0,
    colorEbc: 4.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rye cooked and rolled for brewing, from grain Briess guarantee ergot-free, and pre-gelatinised so no cereal cook is needed. The flavor is concentrated enough that they suggest starting at 5-10 % and stepping up in fives rather than guessing; 40 % is a rye beer. Published at 71 % extract and 7.5 % moisture, converting inside ten minutes.',
    tags: ['adjunct', 'flaked', 'rye', 'american', 'spicy'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Brewers-Rye-Flakes.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-flaked-corn',
    name: 'Brewers Yellow Corn Flakes Non-GMO',
    maker: 'Briess',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80.0,
    moisturePercent: 7.0,
    colorLovibond: 0.8,
    colorEbc: 0.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Non-GMO yellow corn, degermed so the oil that eventually turns a flaked maize rancid is already gone, then pre-gelatinised for the mash tun. Briess publish 80 % extract at 7 % moisture and say the pre-cooked flake yields better and brews more predictably than a conventional one. Up to 40 % lightens color and thins body without costing gravity; expect fresh sweetcorn rather than nothing at all.',
    tags: ['adjunct', 'flaked', 'corn', 'maize', 'american', 'lager', 'light-body'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Brewers-Yellow-Corn-Flakes-NonGMO.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-flaked-brown-rice',
    name: 'Brewers Brown Rice Flakes',
    maker: 'Briess',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 65.0,
    moisturePercent: 7.0,
    colorLovibond: 1.0,
    colorEbc: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Brown rice pre-cooked and rolled, the mildest thing in the Briess flake range and the lowest-yielding: 65 % extract at 7 % moisture, published. Briess ask for a slightly cooler conversion and fifteen extra minutes to get the yield out of it. Up to 40 % dries and lightens a pale lager while leaving barely a flavor of its own, a faint nuttiness aside.',
    tags: ['adjunct', 'flaked', 'rice', 'american', 'lager', 'neutral', 'light-body'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Brewers-Brown-Rice-Flakes.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'briess-torrified-wheat',
    name: 'Brewers Torrified Wheat',
    maker: 'Briess',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 78.0,
    moisturePercent: 7.5,
    colorLovibond: 1.5,
    colorEbc: 2.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Whole wheat heated until the kernel bursts, which breaks the cell walls and lets the malt enzymes at the starch and protein inside. Mill it medium to coarse and mash as usual: Briess publish 78 % extract at 7.5 % moisture and a normal conversion time, both better than raw wheat manages. Up to 40 % for a wit, or a few percent for the head it builds on a bitter; rice hulls keep the lauter honest.',
    tags: ['adjunct', 'wheat', 'torrefied', 'american', 'witbier', 'head-retention'],
    url: 'https://brewingwithbriess.com/wp-content/uploads/documents/Briess-PISB-Brewers-Torrified-Wheat.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'rahr-north-star-pils',
    name: 'North Star Pils',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79.5,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    colorEbc: 3.1,
    diastaticPowerLintner: 135,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Rahr's sweeter pilsner malt: honey and sweet bread where their Premium Pilsner gives grain and cracker, with hay and a nutty edge behind it. Undermodified at a 38-40 Kolbach and built to lauter smoothly, and enzymatic enough at 110-160 Lintner to carry a grist of adjuncts alongside it.",
    tags: ['base', 'pilsner', 'american', 'lager', 'low-colour'],
    url: 'https://rahrmaltingco.com/rahr-north-star-pils/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rahr-standard-2-row',
    name: 'Standard 2-Row',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Midwestern two-row base malt with high extract and plenty of surplus enzyme for corn, rice or flaked adjuncts. Neutral and slightly sweet, it is the default grist filler for American ales and lagers alike.',
    tags: ['base', 'two-row', 'american', 'high-enzyme'],
    url: 'https://rahrmaltingco.com/rahr-the-brewers-standard-2-row/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rahr-premium-pilsner',
    name: 'Premium Pilsner',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81.5,
    moisturePercent: 4,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row barley kilned to continental pilsner color but with the enzyme load of an American malt. A good compromise when a delicate lager grist still has to convert unmalted grain.',
    tags: ['base', 'pilsner', 'lager', 'american'],
    url: 'https://rahrmaltingco.com/rahr-premium-pilsner/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rahr-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.5,
    moisturePercent: 4,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Higher-kilned two-row with a rounder, biscuity malt flavor than the standard product. Designed to be the whole base of a pale ale or IPA while keeping enough diastatic power for a modest adjunct fraction.',
    tags: ['base', 'pale-ale', 'american', 'ipa'],
    url: 'https://rahrmaltingco.com/rahr-the-brewers-standard-pale-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rahr-white-wheat',
    name: 'White Wheat Malt',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 84,
    moisturePercent: 5,
    colorLovibond: 3,
    colorEbc: 6.8,
    diastaticPowerLintner: 160,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted white wheat: very high in extract, protein and enzyme, with a soft bready flavor. It builds dense foam and a silky body, and 5–15 % improves head retention in almost any beer.',
    tags: ['base', 'wheat', 'american', 'head-retention'],
    url: 'https://rahrmaltingco.com/rahr-white-wheat/',
    provenance: 'representative',
  },
  {
    id: 'rahr-munich',
    name: 'Munich Malt',
    maker: 'Rahr',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 10,
    colorEbc: 25.3,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'American-grown Munich malt, kilned for color and Maillard flavor while keeping just enough enzyme to convert itself. Adds a rich, toasty malt center to märzen, dunkel and amber ales.',
    tags: ['munich', 'kilned', 'american', 'malty'],
    provenance: 'representative',
  },
  {
    id: 'rahr-caramel-60',
    name: 'Caramel Malt 60L',
    maker: 'Rahr',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 60,
    colorEbc: 157.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Mid-color crystal malt stewed in the husk to caramelize its sugars before kilning. Contributes toffee sweetness, a copper hue and unfermentable body; 5–10 % is the working range in amber beers.',
    tags: ['crystal', 'caramel', 'american', 'amber'],
    provenance: 'representative',
  },
  {
    id: 'rahr-unmalted-wheat',
    name: 'Unmalted Wheat',
    maker: 'Rahr',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 79,
    moisturePercent: 9.5,
    colorLovibond: 2.5,
    colorEbc: 5.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Raw wheat with no enzyme of its own, so it needs a well-modified base malt alongside it to convert. Prized for the protein haze and thick foam of witbier and lambic; expect a sticky mash and use rice hulls above 25 %.',
    tags: ['adjunct', 'wheat', 'unmalted', 'american', 'witbier', 'haze'],
    url: 'https://rahrmaltingco.com/rahr-unmalted-wheat/',
    provenance: 'representative',
  },
  {
    id: 'rahr-red-wheat',
    name: 'Red Wheat Malt',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.25,
    colorEbc: 4.8,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale wheat malt made from North American hard spring red wheat, with a slight tartness white wheat lacks and smaller, harder kernels that reward careful milling. Contributes the usual wheat-malt sweetness and body along with better head formation and retention; suits any wheat-style beer up to about half the grist.',
    tags: ['base', 'wheat', 'american', 'red-wheat', 'head-retention'],
    url: 'https://rahrmaltingco.com/rahr-red-wheat/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rahr-standard-6-row',
    name: 'Standard 6-Row',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.2,
    colorLovibond: 2.35,
    colorEbc: 5,
    diastaticPowerLintner: 160,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The six-row half of the American brewing tradition: more husk, more protein and more enzyme than any two-row, and correspondingly less extract. That surplus enzyme is the point — it converts a grist loaded with corn, rice, flakes or wheat malt that brings none of its own, and it is what a historical American lager was actually built on. High rates may want a protein rest. Rahr publish no Lintner figure for it, so the diastatic power here is typical of the class.',
    tags: ['base', 'six-row', 'american', 'high-enzyme', 'adjunct', 'lager'],
    url: 'https://rahrmaltingco.com/rahr-standard-6-row/',
    provenance: 'representative',
  },
  {
    id: 'rahr-dextrin-malt',
    name: 'Dextrin Malt',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 2.3,
    colorEbc: 4.9,
    diastaticPowerLintner: 115,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Rahr's answer to chit malt rather than to Carapils: under-modified and high in protein, so it builds body, stable haze and a lasting foam stand while adding almost no color. Ten percent is the rate Rahr talk about for a hazy IPA, and the same addition firms up a pils. Expect bready, grainy, faintly raw-barley flavor at the top of that range. Rahr's page and info sheet disagree on extract and protein; the lower published extract is stored here.",
    tags: ['base', 'dextrin', 'chit', 'under-modified', 'body', 'head-retention', 'american'],
    url: 'https://rahrmaltingco.com/rahr-dextrin-malt/',
    provenance: 'representative',
  },
  {
    id: 'rahr-extra-pale-white-wheat',
    name: 'Extra Pale White Wheat',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 5,
    colorLovibond: 2.25,
    colorEbc: 4.8,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Soft winter white wheat malted paler than Rahr's standard White Wheat, and the highest-extract malt they publish at 83 %. Sweet and lightly doughy, high in protein, and easier to mill than red wheat, which also makes it the better bet for the haze a Belgian white wants. Up to half the grist, or a tenth of it wherever the foam needs help.",
    tags: ['base', 'wheat', 'white-wheat', 'american', 'witbier', 'head-retention', 'low-colour'],
    url: 'https://rahrmaltingco.com/rahr-extra-pale-white-wheat/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rahr-gn-zero',
    name: 'GN Zero Malt',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 6,
    colorLovibond: 2.4,
    colorEbc: 5.2,
    diastaticPowerLintner: 125,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A distilling malt from two-row barley bred free of glycosidic nitrile, the compound that turns into ethyl carbamate in a still — a regulatory problem for spirits, and no problem at all for beer. What a brewer gets is a clean pale base with 125 Lintner behind it: earthy and grainy, with bread crumb and a green, sprouted note underneath.',
    tags: ['base', 'two-row', 'american', 'distilling', 'gn-free', 'high-enzyme'],
    url: 'https://rahrmaltingco.com/rahr-gn-zero-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rahr-gn-zero-high-enzyme-distillers',
    name: 'GN Zero High Enzyme Distillers Malt',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 6.5,
    colorLovibond: 2.05,
    colorEbc: 4.2,
    diastaticPowerLintner: 250,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The most enzymatic malt in this catalog: Rahr publish 200-300 Lintner and over 80 dextrinising units of alpha amylase, built for a bourbon mash that is mostly corn. A brewer will never need that much, but a few percent will convert a grist of flakes, oats or rye that nothing else in the tun can reach. Glycosidic-nitrile free, and neutral in flavor by design.',
    tags: ['base', 'distilling', 'high-enzyme', 'american', 'gn-free', 'adjunct'],
    url: 'https://rahrmaltingco.com/rahr-gn-zero-high-enzyme-distillers-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'rahr-high-dp-distillers',
    name: 'High DP Distillers Malt',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 2.95,
    colorEbc: 6.6,
    diastaticPowerLintner: 220,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rahr say it plainly: this is not a source of flavor or extract, it is a source of enzyme. Over 220 Lintner, high alpha amylase and plenty of free amino nitrogen, sold to convert large fractions of unmalted corn, wheat or rye flakes and to feed the yeast while doing it. Ten to twenty percent of a heavily adjuncted grist is the brewing use. Page and info sheet disagree on color and moisture; the product page figures are stored.',
    tags: ['base', 'distilling', 'high-enzyme', 'american', 'adjunct', 'fan'],
    url: 'https://rahrmaltingco.com/rahr-high-dp-distillers-malt/',
    provenance: 'representative',
  },
  {
    id: 'rahr-malted-oats',
    name: 'Malted Oats',
    maker: 'Rahr',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 60,
    moisturePercent: 6,
    colorLovibond: 2.05,
    colorEbc: 4.2,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Oats taken through a full malting, which is what separates them from flaked oats: a little diastatic power of their own, an earthier grain flavor, and low beta-glucan, so the sticky lauter oats are famous for is much less likely. The extract is genuinely low at 60 % — a fifth of the grist costs you gravity — and what you buy with it is silky body and a head that stands. Hazy IPA, stout and porter.',
    tags: ['adjunct', 'oats', 'malted', 'american', 'mouthfeel', 'hazy', 'head-retention'],
    url: 'https://rahrmaltingco.com/rahr-malted-oats/',
    provenance: 'representative',
  },
  {
    id: 'rahr-old-world-pilsner',
    name: 'Old World Pilsner',
    maker: 'Rahr',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4,
    colorLovibond: 1.65,
    colorEbc: 3.2,
    diastaticPowerLintner: 115,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "North American two-row germinated deliberately slowly, so more insoluble protein stays in the kernel: less extract than Rahr's other pilsner malts, and in exchange more flavor, more yeast nutrition and better foam. The lower diastatic power is a feature if you step-mash, since some starch is left to convert at the higher rest. Rahr's page and info sheet disagree slightly on color and moisture; the page figures are stored.",
    tags: ['base', 'pilsner', 'american', 'lager', 'under-modified', 'step-mash'],
    url: 'https://rahrmaltingco.com/rahr-old-world-pilsner/',
    provenance: 'representative',
  },
  {
    id: 'rahr-to-thee-pils',
    name: 'To Thee! Pils',
    maker: 'Rahr',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79.5,
    moisturePercent: 4.5,
    colorLovibond: 1.5,
    colorEbc: 2.8,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Rahr's Canadian pilsner malt, made in Alix, Alberta from Canadian barley, and the palest thing they sell at 1.3-1.75 SRM. Low color and low modification, meant for beers where the hop or the yeast is supposed to be the story: continental and craft lagers of any stripe, and a pale West Coast IPA. Grain, dough, golden bread and a faint nuttiness. No diastatic power is published, so the figure here is class-typical.",
    tags: ['base', 'pilsner', 'canadian', 'lager', 'low-colour', 'west-coast-ipa'],
    url: 'https://rahrmaltingco.com/rahr-to-thee-pils/',
    provenance: 'representative',
  },
  {
    id: 'rahr-unmalted-red-wheat',
    name: 'Unmalted Red Wheat',
    maker: 'Rahr',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 79,
    moisturePercent: 13,
    colorLovibond: 2.5,
    colorEbc: 5.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Raw hard red wheat: harder, smaller kernels than the white grain and a slightly tart, fruity edge to the grain flavor. The starch is ungelatinised and the protein high, so it wants a protein rest or a cereal mash and a well-modified base malt to convert it. Half the grist is Rahr's ceiling; lambic and Flanders red are where it belongs, along with anything that needs haze and foam. Rahr publish neither extract nor color for it, so both are class-typical here.",
    tags: ['adjunct', 'wheat', 'unmalted', 'red-wheat', 'american', 'lambic', 'haze'],
    url: 'https://rahrmaltingco.com/rahr-unmalted-red-wheat/',
    provenance: 'representative',
  },
  {
    id: 'great-western-premium-2-row',
    name: 'Premium 2-Row',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.5,
    moisturePercent: 4,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pacific Northwest two-row base malt, clean and pale with generous diastatic power. Behaves like any American 2-row in the mash and is the standard grist backbone for West Coast breweries.',
    tags: ['base', 'two-row', 'american', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/premium-two-row/',
    provenance: 'representative',
  },
  {
    id: 'great-western-pure-idaho-pilsner',
    name: 'Pure Idaho Pilsner',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.2,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 130,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Single-origin pilsner malt from irrigated Idaho barley, kilned low for minimal color. Gives a clean, lightly sweet lager base with more enzyme headroom than a continental pilsner malt.',
    tags: ['base', 'pilsner', 'lager', 'american', 'idaho'],
    url: 'https://www.greatwesternmalting.com/product/pure-idaho-pilsner-malt/',
    provenance: 'representative',
  },
  {
    id: 'great-western-full-pint-pale',
    name: 'Full Pint Pale Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 4,
    colorLovibond: 2.6,
    colorEbc: 5.7,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Made from the Full Pint barley variety bred at Oregon State, which malts to unusually high extract. Rounder and slightly sweeter than commodity 2-row, so it rewards being the sole base malt in a hop-forward beer.',
    tags: ['base', 'pale', 'american', 'single-variety', 'northwest'],
    provenance: 'representative',
  },
  {
    id: 'great-western-northwest-pale-ale',
    // Sold today as Pale High Color Malt; the id is kept for continuity since
    // recipes store it.
    name: 'Pale High Color Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.0,
    moisturePercent: 4.5,
    colorLovibond: 3.0,
    colorEbc: 6.7,
    diastaticPowerLintner: 115,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row kilned with traditional British practice for a deeper gold and a rounder, maltier flavor than the base 2-row. A drop-in base for a hop-forward or bigger beer that wants more malt backbone than plain 2-row without reaching for crystal.',
    tags: ['base', 'pale-ale', 'northwest-pale-ale', 'american', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/pale-high-color/',
    provenance: 'spec-sheet',
  },
  {
    id: 'great-western-vienna',
    name: 'Vienna Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.5,
    moisturePercent: 4,
    colorLovibond: 4,
    colorEbc: 9.4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Lightly kilned amber malt that retains enough enzyme to stand as a full base. Adds a golden tint and a toasted, faintly sweet grain flavor to Vienna lager, märzen and malty pale ales.',
    tags: ['vienna', 'kilned', 'american', 'base'],
    url: 'https://www.greatwesternmalting.com/product/vienna-malt/',
    provenance: 'representative',
  },
  {
    id: 'great-western-munich-10l',
    // Sold today as Light Munich Malt; the id is kept for continuity since
    // recipes store it.
    name: 'Light Munich Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.0,
    colorLovibond: 6.0,
    colorEbc: 14.7,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Gently kilned Munich, paler and considerably more enzyme-rich than most Munich malts, so it can carry an entire grist rather than just season one. Cracker and bread notes over a deepening gold; 10–30 % for a lift of malt depth, or most of the grist in an Oktoberfest or dunkel.',
    tags: ['munich', 'kilned', 'american', 'malty', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/light-munich/',
    provenance: 'spec-sheet',
  },
  {
    id: 'great-western-crystal-60',
    name: 'Crystal Malt 60L',
    maker: 'Great Western',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 60,
    colorEbc: 157.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Medium crystal malt with a clean caramel and light dried-fruit sweetness. Steeps or mashes equally well and is the usual way to put color and residual body into an amber or red ale at 5–10 %.',
    tags: ['crystal', 'caramel', 'american', 'amber'],
    provenance: 'representative',
  },
  {
    id: 'great-western-superior-pilsen',
    name: 'Superior Pilsen Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 82.0,
    moisturePercent: 4.5,
    colorLovibond: 1.5,
    colorEbc: 2.8,
    diastaticPowerLintner: 125,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Lower-protein barley and a delicate kilning regime give this the highest extract and lowest color in the GWM base line: crisp, straw-pale and faintly sweet. A clean all-purpose base for pale lagers and light ales that want nothing standing in front of the hops.',
    tags: ['base', 'pilsner', 'lager', 'american', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/superior-pilsen/',
    provenance: 'spec-sheet',
  },
  {
    id: 'great-western-dextra-pils',
    name: 'Dextra Pils',
    maker: 'Great Western',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 7.0,
    colorLovibond: 1,
    colorEbc: 1.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "A dextrin malt made by gelatinising the kernel's starch rather than crystallizing it, so it thickens body and stabilizes foam without adding color or flavor. A pale lager's answer to Carapils; GWM does not publish an extract figure, so the yield here is class-typical.",
    tags: ['dextrin', 'american', 'body', 'head-retention', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/dextra-pils/',
    provenance: 'representative',
  },
  {
    id: 'great-western-high-enzyme',
    name: 'High-Enzyme Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 5.5,
    colorLovibond: 1.85,
    colorEbc: 3.7,
    diastaticPowerLintner: 260,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A distilling malt bred for maximum enzyme, not flavor: high protein and an enormous diastatic power built to convert a mash heavy with corn or other adjunct. Useful in beer only where a grist needs serious extra conversion power; GWM publish DP but not extract, so the yield here is class-typical; the 260 Lintner is theirs.',
    tags: ['base', 'high-enzyme', 'american', 'distilling', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/high-enzyme-malt/',
    provenance: 'representative',
  },
  {
    id: 'great-western-pure-oregon',
    name: 'Pure Oregon Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.0,
    moisturePercent: 4.5,
    colorLovibond: 1.95,
    colorEbc: 4.0,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A single-origin base malt made entirely from Oregon-grown barley, fully modified and gently dried for a clean, balanced sweetness. Behaves like any American 2-row in the mash; the appeal is provenance rather than a different flavor.',
    tags: ['base', 'american', 'oregon', 'single-origin', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/pure-oregon-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'great-western-pure-washington',
    name: 'Pure Washington Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.0,
    moisturePercent: 4.5,
    colorLovibond: 1.95,
    colorEbc: 4.0,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Pure Washington's Palouse-grown counterpart to Pure Oregon Malt, with the same fully-modified, gently-dried process and near-identical published figures. A single-origin base for brewers who want the story on the label to match the barley in the mash tun.",
    tags: ['base', 'american', 'washington', 'single-origin', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/pure-washington-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'great-western-non-gn',
    name: 'Non-GN Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82.5,
    moisturePercent: 4.5,
    colorLovibond: 1.85,
    colorEbc: 3.7,
    diastaticPowerLintner: 125,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted from Odyssey and other barley varieties bred low in glycosidic nitrile, the compound that can carry through distillation as ethyl carbamate — the malt exists for American single-malt whiskey producers, but its high extract and diastatic power make it a perfectly capable pale base malt for beer too.',
    tags: ['base', 'american', 'distilling', 'non-gn', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/non-gn-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'great-western-pink-boots',
    name: 'Pink Boots Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 83,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    colorEbc: 3.0,
    diastaticPowerLintner: 130,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A seasonal, low-color Pilsen-style base malt sold each year from a different barley variety — the 2024 release used Odyssey — with a share of every bag going to the Pink Boots Society scholarship fund. Brews like any clean, high-extract American base malt; GWM does not publish diastatic power, so that figure is class-typical.',
    tags: ['base', 'pilsner', 'american', 'seasonal', 'charity', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/pink-boots-malt/',
    provenance: 'representative',
  },
  {
    id: 'great-western-mela',
    name: 'Mela Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.0,
    colorLovibond: 30,
    colorEbc: 78.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A melanoidin-style malt kilned hot for bold, malt-forward flavor: amber-brown, slightly sweet and toasted, with a biscuit edge. Works well up to 40 % alongside a base malt in dunkel lager, Scotch ale, doppelbock or a malty red ale. GWM do not publish diastatic power, so it is treated as spent here.',
    tags: ['munich', 'kilned', 'melanoidin', 'american', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/mela-malt/',
    provenance: 'representative',
  },
  {
    id: 'great-western-brumalt',
    name: 'Brūmalt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.0,
    moisturePercent: 6.5,
    colorLovibond: 20,
    colorEbc: 51.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "GWM's own honey malt: deep amber, tart-sweet and fruity in a way plainer kilned malts never reach, with honey and stone-fruit notes over a malty base. A few percent lifts IPAs, saisons, pale and Belgian ales without leaning on crystal malt for it. Diastatic power is not published and is treated as spent.",
    tags: ['honey', 'kilned', 'fruity', 'american', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/brumalt/',
    provenance: 'representative',
  },
  {
    id: 'great-western-chit',
    name: 'Chit Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 50.0,
    moisturePercent: 5.0,
    colorLovibond: 2.0,
    colorEbc: 4.1,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Premium 2-row barley germinated only to the chit stage, so the large foam-positive proteins and beta-glucan survive that full modification would break down. Added at up to 10 % it builds haze, body and head retention. The published 50 % extract is low by design — this is a functional additive, not a fermentable base — and GWM do not publish a diastatic power, so it should never be counted on to convert anything.',
    tags: [
      'base',
      'chit',
      'foam',
      'body',
      'head-retention',
      'american',
      'under-modified',
      'northwest',
    ],
    url: 'https://www.greatwesternmalting.com/product/chit-malt/',
    provenance: 'representative',
  },
  {
    id: 'great-western-dark-munich',
    name: 'Dark Munich Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.0,
    colorLovibond: 20,
    colorEbc: 51.8,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned harder than Light Munich for deep amber color, mild roast and chocolate hints behind a base of toasted biscuit and subtle sweetness. Works up to half the grist in a dunkel lager or bock; GWM do not publish diastatic power for it, so the figure here is class-typical and modest, in line with a malt this dark.',
    tags: ['munich', 'kilned', 'dark', 'american', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/dark-munich-malt/',
    provenance: 'representative',
  },
  {
    id: 'great-western-caramel-steam',
    name: 'Caramel Steam Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77.0,
    moisturePercent: 4.5,
    colorLovibond: 40,
    colorEbc: 104.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A crystal malt made by an alternative steam process rather than the traditional stew-and-roast drum, aimed at a softer, less astringent caramel character. Copper-red with graham-cracker and nutty caramel notes; use it where a traditional crystal malt would land too sharp — IPAs, Irish red, English bitter, porter, barleywine.',
    tags: ['crystal', 'caramel', 'steam', 'american', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/caramel-steam-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'great-western-crystal-15',
    name: 'Crystal 15 Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77.0,
    moisturePercent: 7.8,
    colorLovibond: 15,
    colorEbc: 38.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "GWM's lightest crystal grade, roasted in the same custom drums as their darker crystals but stopped early for a bright, light caramel sweetness with only a delicate malt edge. A gentler alternative to a 20L crystal in pale ales, lagers, Vienna and Oktoberfest beers.",
    tags: ['crystal', 'caramel', 'american', 'northwest', 'pale'],
    url: 'https://www.greatwesternmalting.com/product/crystal-15-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'great-western-biscuit-rye',
    name: 'Biscuit Rye Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.5,
    colorLovibond: 20,
    colorEbc: 51.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      '100 % malted rye drum-roasted in the biscuit family: toasted and bready with the peppery spice rye brings, and copper colored rather than dark. A few percent adds rye spice to a pale ale or IPA; push toward 30 % for a fully rye-forward amber, porter or stout. Diastatic power is not published and is treated as spent.',
    tags: ['rye', 'toasted', 'biscuit', 'spicy', 'american', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/biscuit-rye-malt/',
    provenance: 'representative',
  },
  {
    id: 'great-western-white-wheat',
    name: 'White Wheat Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 86.0,
    moisturePercent: 5.0,
    colorLovibond: 3.7,
    colorEbc: 8.6,
    diastaticPowerLintner: 135,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "GWM's premium wheat malt, grown in the Pacific Northwest and kilned for a soft, bready sweetness and a published extract at the top of the wheat-malt range. Standard at 50–60 % in an American wheat beer or Weizen; husk-free, so use rice hulls above about 40 %.",
    tags: ['base', 'wheat', 'american', 'head-retention', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/white-wheat-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'great-western-low-color-wheat',
    name: 'Low Color Wheat Malt',
    maker: 'Great Western',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 85.0,
    moisturePercent: 5.0,
    colorLovibond: 1.5,
    colorEbc: 2.8,
    diastaticPowerLintner: 130,
    suggestedMaxPercent: 80,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A wheat malt kilned even lighter than the standard White Wheat, aimed at Hefeweizens, American wheats and witbiers that want body and head retention without adding any color at all. GWM do not publish diastatic power, so it is estimated from their other wheat malts.',
    tags: ['base', 'wheat', 'american', 'head-retention', 'low-colour', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/low-color-wheat/',
    provenance: 'representative',
  },
  {
    id: 'great-western-white-wheat-unmalted',
    name: 'White Wheat Unmalted',
    maker: 'Great Western',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 79,
    moisturePercent: 10.0,
    colorLovibond: 2.2,
    colorEbc: 4.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Raw Pacific Northwest white wheat with no enzyme of its own, so it rides alongside a well-modified base malt to convert. Adds body, foam stability and the cloudy haze that hazy pale ales, New England IPAs, Belgian wit and Weissbier are built around; expect a sticky mash and add rice hulls above about 25 %.',
    tags: ['adjunct', 'wheat', 'unmalted', 'american', 'haze', 'northwest'],
    url: 'https://www.greatwesternmalting.com/product/white-wheat-unmalted/',
    provenance: 'representative',
  },
];
